import React from 'react';
import { MapPin } from 'lucide-react';
import { CodeChoice } from './CodeChoice';

const nameOf = (suggestion) => suggestion.slot_name || suggestion.slot_code;

/**
 * Where the server suggests leaving something, best first, with why, and up
 * to three more. On the step that asks where it goes each one is a button
 * that reads its code, as scanning its label would; elsewhere they are only
 * read.
 *
 * @param {object} props
 * @param {Array<{slot_code: string, slot_name?: string, reasons?: string[]}>} props.suggestions
 * @param {boolean} [props.interactive=false]
 * @param {boolean} [props.disabled=false] - The screen takes no read now.
 * @param {(code: string) => void} [props.onTap]
 * @param {string} props.testId
 */
export const SlotSuggestions = ({ suggestions, interactive = false, disabled = false, onTap, testId }) => {
  if (!suggestions?.length) return null;
  const [best, ...rest] = suggestions;
  const others = rest.slice(0, 3);
  const reasons = best.reasons?.length > 0 ? best.reasons.join(' · ') : null;

  return (
    <div data-testid={testId} className="space-y-2 rounded-2xl bg-ninja-gray p-3 dark:bg-[#2d2d2d]">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Destino sugerido</p>
      {interactive ? (
        <CodeChoice
          code={best.slot_code}
          label={nameOf(best)}
          detail={reasons}
          icon={MapPin}
          disabled={disabled}
          onTap={onTap}
          testId="slot-suggestion"
          data-best="true"
        />
      ) : (
        <div>
          <p className="flex items-center gap-1.5 text-base font-bold text-ninja-dark dark:text-white">
            <MapPin size={16} aria-hidden="true" /> {nameOf(best)}
          </p>
          {reasons && <p className="text-sm text-text-secondary">{reasons}</p>}
        </div>
      )}
      {others.length > 0 &&
        (interactive ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
            {'También: '}
            {/* A chip fills its span, which is as wide as the slot's name. */}
            {others.map((item) => (
              <span key={item.slot_code}>
                <CodeChoice
                  code={item.slot_code}
                  label={nameOf(item)}
                  pill
                  disabled={disabled}
                  onTap={onTap}
                  testId="slot-suggestion"
                  data-best="false"
                />
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-secondary">También: {others.map(nameOf).join(', ')}</p>
        ))}
    </div>
  );
};
